import { createContext, useContext, type ReactNode } from 'react'
import { useCache } from '../hooks/useCache'

type CacheUtils<T = unknown> = ReturnType<typeof useCache<T>>

type CacheContextValue = {
  resort: CacheUtils
  subfolders: CacheUtils
  dailyAnalytics: CacheUtils
  seasonalWeekly: CacheUtils
  seasonalWorkType: CacheUtils
  seasonalWinch: CacheUtils
  seasonalSurface: CacheUtils
  seasonalFuel: CacheUtils
  seasonalFleet: CacheUtils
  titilerColormaps: CacheUtils
}

const TITILER_COLORMAPS_CACHE_MS = Number.MAX_SAFE_INTEGER

/**
 * Cache Context
 * Provides global cache utilities for different cache types
 */
const CacheContext = createContext<CacheContextValue | null>(null)

type CacheProviderProps = {
  children: ReactNode
}

/**
 * Cache Provider
 * Provides cache utilities for resorts, folders, and other cached data
 */
export const CacheProvider = ({ children }: CacheProviderProps) => {
  const resortCache = useCache('resort_cache_', 30 * 60 * 1000)
  const subfoldersCache = useCache('operational_subfolders_', 5 * 60 * 1000)
  const dailyAnalyticsCache = useCache('grooming_daily_', 5 * 60 * 1000)
  const seasonalWeeklyCache = useCache('seasonal_weekly_', 5 * 60 * 1000)
  const seasonalWorkTypeCache = useCache('seasonal_work_type_', 5 * 60 * 1000)
  const seasonalWinchCache = useCache('seasonal_winch_', 5 * 60 * 1000)
  const seasonalSurfaceCache = useCache('seasonal_surface_', 5 * 60 * 1000)
  const seasonalFuelCache = useCache('seasonal_fuel_', 5 * 60 * 1000)
  const seasonalFleetCache = useCache('seasonal_fleet_', 5 * 60 * 1000)
  const titilerColormapsCache = useCache(
    'titiler_colormaps_',
    TITILER_COLORMAPS_CACHE_MS,
  )

  const value: CacheContextValue = {
    resort: resortCache,
    subfolders: subfoldersCache,
    dailyAnalytics: dailyAnalyticsCache,
    seasonalWeekly: seasonalWeeklyCache,
    seasonalWorkType: seasonalWorkTypeCache,
    seasonalWinch: seasonalWinchCache,
    seasonalSurface: seasonalSurfaceCache,
    seasonalFuel: seasonalFuelCache,
    seasonalFleet: seasonalFleetCache,
    titilerColormaps: titilerColormapsCache,
  }

  return <CacheContext.Provider value={value}>{children}</CacheContext.Provider>
}

/**
 * Hook to use cache context
 */
export const useCacheContext = (): CacheContextValue => {
  const context = useContext(CacheContext)
  if (!context) {
    throw new Error('useCacheContext must be used within CacheProvider')
  }
  return context
}

/**
 * Convenience hooks for specific cache types
 */
export const useResortCache = (): CacheUtils => {
  const { resort } = useCacheContext()
  return resort
}

export const useSubfoldersCache = (): CacheUtils => {
  const { subfolders } = useCacheContext()
  return subfolders
}

export const useDailyAnalyticsCache = (): CacheUtils => {
  const { dailyAnalytics } = useCacheContext()
  return dailyAnalytics
}

export const useSeasonalWeeklyCache = (): CacheUtils => {
  const { seasonalWeekly } = useCacheContext()
  return seasonalWeekly
}

export const useSeasonalWorkTypeCache = (): CacheUtils => {
  const { seasonalWorkType } = useCacheContext()
  return seasonalWorkType
}

export const useSeasonalWinchCache = (): CacheUtils => {
  const { seasonalWinch } = useCacheContext()
  return seasonalWinch
}

export const useSeasonalSurfaceCache = (): CacheUtils => {
  const { seasonalSurface } = useCacheContext()
  return seasonalSurface
}

export const useSeasonalFuelCache = (): CacheUtils => {
  const { seasonalFuel } = useCacheContext()
  return seasonalFuel
}

export const useSeasonalFleetCache = (): CacheUtils => {
  const { seasonalFleet } = useCacheContext()
  return seasonalFleet
}

export const useTitilerColormapsCache = (): CacheUtils => {
  const { titilerColormaps } = useCacheContext()
  return titilerColormaps
}
